import type { ReactNode } from 'react';
import { ProjectFolderArt, type FolderBrand } from '../ui/ProjectFolderArt';
import { tip } from '../ui/tooltip';
import type { ProjectInfo, ProjectOverview } from './rows';
import './projectCards.css';

/** "3 Artwork files" / "No Artwork files yet"; "Open project" while the count is unknown. */
export function filesLine(files: number | null | undefined): string {
  if (files === null || files === undefined) return 'Open project';
  if (files === 0) return 'No Artwork files yet';
  return `${files} Artwork ${files === 1 ? 'file' : 'files'}`;
}

/** The pictures a project's pocket holds: its cover in front, then its newest files. */
export function pocketPictures(cover: string | null | undefined, thumbnails: readonly string[], files: number | null): (string | null)[] {
  const behind = Array.from({ length: Math.min(files ?? thumbnails.length, 3) }, (_, index) => thumbnails[index] ?? null);
  return cover ? [cover, ...behind].slice(0, 3) : behind;
}

/**
 * A project as Artwork's folder: a photo pocket whose front picture is the project's own cover,
 * with its newest Artwork files behind it (no cover: the files alone), the brand's logo on its
 * lower right, its name and one quiet line under it. Never a colour. `trailing` sits beside the
 * name (the ⋯, an Unpin) and shows under the pointer or while `held`.
 */
export function FolderCard({
  name,
  line,
  covers,
  brand,
  onOpen,
  trailing,
  held = false,
  dim = false,
}: {
  name: string;
  line: string;
  covers: readonly (string | null)[];
  brand?: FolderBrand | null;
  onOpen: () => void;
  trailing?: ReactNode;
  held?: boolean;
  dim?: boolean;
}) {
  return (
    <div className={`home-project-card ${held ? 'is-held' : ''} ${dim ? 'opacity-60' : ''}`}>
      <button type="button" onClick={onOpen} className="home-project-folder" data-project-folder aria-label={`Open ${name} · ${line}`} {...(trailing ? {} : tip(`${name} · ${line}`))}>
        <ProjectFolderArt covers={covers} brand={brand} />
        <span className={`home-project-caption ${trailing ? 'pr-[32px]' : ''}`}>
          <span className="home-project-name">{name}</span>
          <span className="home-project-count">{line}</span>
        </span>
      </button>
      {trailing ? <span className="home-project-trailing">{trailing}</span> : null}
    </div>
  );
}

/** A project on the Home's Projects row. */
export function ProjectStack({ summary, project, brand, onOpen }: { summary: ProjectOverview; project: ProjectInfo; brand?: FolderBrand | null; onOpen: () => void }) {
  return <FolderCard name={project.name} line={filesLine(summary.files)} covers={pocketPictures(project.cover, summary.thumbnails, summary.files)} brand={brand} onOpen={onOpen} />;
}

/** Match the final folder proportions while the project list loads. */
export function PlaceholderStack() {
  return (
    <div aria-hidden className="home-project-folder home-project-placeholder">
      <ProjectFolderArt />
      <span className="home-project-caption">
        <span className="block h-[18px] w-[66%] rounded-[4px] bg-fill-2" />
        <span className="block h-[16px] w-[44%] rounded-[4px] bg-fill-2" />
      </span>
    </div>
  );
}
